import { forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectFeatureFlags } from 'core/userContext';
import { RowWrapper } from 'modules/workflowNodeEditors/EditorBody.styled';
import { ChevronDown } from 'ui/icons';
import { BasicSelect } from 'ui/selects';
import { Label } from 'ui/theme';

import { DropdownContent } from './DropdownContent';
import {
  SELECTIVE_TARGETING_OPTIONS,
  SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS,
  SelectiveTargetingOptionValues,
} from './SelectiveTargeting.constants';
import {
  AppsCounter,
  AppsListPreview,
  AppsListSubtextContainer,
  DropdownButton,
  SelectiveTargetingSection,
  StyledDropdownContentContainer,
  TriggerPreview,
} from './SelectiveTargeting.styled';
import {
  SelectiveTargetingBaseProps,
  SelectiveTargetingDropdownTriggerProps,
  SelectiveTargetingOption,
} from './SelectiveTargeting.types';
import {
  combineSubtextVisibleListWithOverflow,
  getAppsForSelectedOption,
  getAppsListSubtext,
} from './utils';

const getTargetingOptionFromValue = (value: SelectiveTargetingOptionValues) =>
  SELECTIVE_TARGETING_OPTIONS.find((option) => option.value === value) ||
  SELECTIVE_TARGETING_OPTIONS[0];

const getTargetingOptionFromMobileAppIds = (ids: number[]) => {
  let value = SelectiveTargetingOptionValues.ALL_MOBILE_APPS;
  if (ids.length === 1) {
    if (ids[0] === -2) {
      value = SelectiveTargetingOptionValues.ALL_WEBSITES;
    } else if (ids[0] === -1) {
      value = SelectiveTargetingOptionValues.EVERYTHING;
    } else if (ids[0] === 0) {
      value = SelectiveTargetingOptionValues.OTT;
    } else {
      value = SelectiveTargetingOptionValues.CUSTOM;
    }
  } else if (ids.length > 1) {
    value = SelectiveTargetingOptionValues.CUSTOM;
  }
  return getTargetingOptionFromValue(value);
};

export const SelectiveInApp = ({
  mobileApps = [],
  mobileAppIds,
  setMobileAppIds,
  setIsSelectiveTargetingEnabled,
  isDisabled = false,
}: Omit<SelectiveTargetingBaseProps, 'isSelectiveTargetingEnabled'>) => {
  const featureFlags = useSelector(selectFeatureFlags);
  const shouldIncludeOTT = featureFlags.RokuTemplating;

  const [selectedTargetingOption, setSelectedTargetingOption] = useState(
    getTargetingOptionFromMobileAppIds(mobileAppIds),
  );

  const onChangeSelectedTargetingOption = (option: SelectiveTargetingOption) => {
    let appIds: number[] = [];
    switch (option.value) {
      case SelectiveTargetingOptionValues.ALL_WEBSITES:
        setIsSelectiveTargetingEnabled(true);
        appIds = [-2];
        break;
      case SelectiveTargetingOptionValues.EVERYTHING:
        setIsSelectiveTargetingEnabled(true);
        appIds = [-1];
        break;
      case SelectiveTargetingOptionValues.OTT:
        setIsSelectiveTargetingEnabled(true);
        appIds = [0];
        break;
      case SelectiveTargetingOptionValues.CUSTOM:
        setIsSelectiveTargetingEnabled(true);
        break;
      default:
        // default to ALL_MOBILE_APPS which is selective targeting turned off
        setIsSelectiveTargetingEnabled(false);
    }
    setMobileAppIds(appIds);
    setSelectedTargetingOption(option);
  };

  /* Filtering out the "OTT" option if RokuTemplating feature flag is not on */
  const getSelectiveTargetingOptions = () =>
    SELECTIVE_TARGETING_OPTIONS.filter(
      (o) => !!shouldIncludeOTT || o.value !== SelectiveTargetingOptionValues.OTT,
    );

  const getDropdownContent = () => (
    <StyledDropdownContentContainer>
      <DropdownContent
        selectedTargetingOption={selectedTargetingOption}
        onChangeSelectedTargetingOption={onChangeSelectedTargetingOption}
        targetingOptions={getSelectiveTargetingOptions()}
        mobileApps={mobileApps}
        mobileAppIds={mobileAppIds}
        setMobileAppIds={setMobileAppIds}
      />
    </StyledDropdownContentContainer>
  );

  const Trigger = forwardRef<HTMLButtonElement, SelectiveTargetingDropdownTriggerProps>(
    ({ toggleDropdown }, ref) => (
      <DropdownButton ref={ref} onClick={toggleDropdown}>
        <TriggerPreview>
          {selectedTargetingOption.name}
          <AppsCounter>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? mobileAppIds.length
              : getAppsForSelectedOption(selectedTargetingOption, mobileApps).length}
          </AppsCounter>
        </TriggerPreview>
        <ChevronDown height={16} width={16} />
      </DropdownButton>
    ),
  );

  return (
    <RowWrapper>
      <SelectiveTargetingSection>
        <Label>Destination</Label>
        <BasicSelect
          id="in-app-destination-select"
          Option={getDropdownContent}
          options={SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS}
          Trigger={Trigger}
          isDisabled={isDisabled}
          isFullWidth
        />
        <AppsListPreview>
          <AppsListSubtextContainer>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? combineSubtextVisibleListWithOverflow(
                  mobileApps.filter((app) => mobileAppIds.find((id) => app.id === id)),
                )
              : getAppsListSubtext(selectedTargetingOption, mobileApps)}
          </AppsListSubtextContainer>
        </AppsListPreview>
      </SelectiveTargetingSection>
    </RowWrapper>
  );
};
